import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  RefreshCw, TrendingUp, Activity, Zap, Users,
  CheckCircle2, AlertTriangle, BarChart3, Loader, Calendar,
  ArrowUp, ArrowDown, Minus, ChevronDown
} from 'lucide-react';

const MIS_SHEET_ID =
  process.env.REACT_APP_MIS_SHEET_ID || '1i3Uo-QbY5IcLuYTkO6hn6tFDr-4-gMvtt83KP6HrNow';
const MIS_SHEET_NAME = 'TESTMASTER';

// ── Column indices (0-based) ──────────────────────────────────────────────────
const C = {
  NAME: 3, WEEK: 4, MONTH: 5, YEAR: 6, STATUS: 7,
  // FMS (delayed plan/actual are durations, "176:30:00")
  FMS_WD_PLAN: 8,  FMS_WD_ACT: 9,   FMS_WD_PCT: 10,
  FMS_OT_PLAN: 11, FMS_OT_ACT: 12,  FMS_OT_PCT: 13,
  FMS_DEL_PLAN: 14, FMS_DEL_ACT: 15, FMS_DEL_PCT: 16,
  // Delegation
  DEL_PLAN: 17, DEL_ACT: 18, DEL_PCT: 19,
  DEL_RED: 20,  DEL_YEL: 21,  DEL_GRN: 22,
  // Checklist AVG (AZ–BC = 51–54)
  CL_PLAN: 51, CL_ACT: 52, CL_NOT_DONE: 53, CL_NOT_DONE_OT: 54,
  // HelpTicket (BD–BL = 55–63)
  HT_WD_PLAN: 55, HT_WD_ACT: 56, HT_WD_PCT: 57,
  HT_OT_PLAN: 58, HT_OT_ACT: 59, HT_OT_PCT: 60,
  HT_DEL_PLAN: 61, HT_DEL_ACT: 62, HT_DEL_PCT: 63,
  // AVG scores (BM–BP = 64–67)
  AVG_FMS: 64, AVG_HT: 65, AVG_DEL: 66, AVG_CL: 67,
  // Sales (BQ–BY = 68–76)
  SALES_TOT_TGT: 68, SALES_NEW_TGT: 69, SALES_EX_TGT: 70,
  SALES_TOT_ACH: 71, SALES_NEW_ACH: 72, SALES_EX_ACH: 73,
  SALES_NOT_ACH: 74, SALES_NEW_NOT_ACH: 75, SALES_EX_NOT_ACH: 76,
  // Defined weightage (BZ–CD = 77–81)
  WGT_DEL: 77, WGT_CL: 78, WGT_FMS: 79, WGT_HT: 80, WGT_SALES: 81,
  // Weighted scores (CE–CI = 82–86)
  WSCORE_FMS: 82, WSCORE_HT: 83, WSCORE_DEL: 84, WSCORE_CL: 85, WSCORE_SALES: 86,
  // Overall (CJ = 87)
  OVERALL: 87,
};

// ── Value helpers ─────────────────────────────────────────────────────────────
// Some TESTMASTER columns store "not done" as a negative number. n() returns
// them on the scale this page displays, so every reader gets the same value:
//   - Component averages (-100..0, 0 = nothing missed) → a 0–100 score, higher
//     is better: weighted score = weightage × score / 100, and the overall
//     score is the sum of the weighted scores.
//   - FMS / HelpTicket "% Score (Not Done)" (-100..0) → a positive % not done.
//   - Delegation TaskPercentage (-1..0, -1 = nothing done) → the fraction done.
const COMPONENT_SCORE_COLS = new Set([C.AVG_FMS, C.AVG_HT, C.AVG_DEL, C.AVG_CL]);
const NEGATIVE_NOT_DONE_COLS = new Set([
  C.FMS_WD_PCT, C.FMS_OT_PCT, C.FMS_DEL_PCT, C.HT_WD_PCT, C.HT_OT_PCT, C.HT_DEL_PCT,
]);

const n = (row, col) => {
  const v = row?.[col];
  if (v === undefined || v === null || v === '') return null;
  const p = parseFloat(String(v).replace('%', '').replace(/,/g, ''));
  if (isNaN(p)) return null;
  if (COMPONENT_SCORE_COLS.has(col)) return 100 + p;
  if (NEGATIVE_NOT_DONE_COLS.has(col)) return Math.abs(p);
  if (col === C.DEL_PCT) return 1 + p;
  return p;
};
const fmt  = (val, suf = '%', dec = 1) => (val === null || val === undefined) ? '—' : `${Number(val).toFixed(dec)}${suf}`;
const fmtN = (val) => (val === null || val === undefined) ? '—' : Number(val).toLocaleString('en-IN');

// ── Score colour & grade ──────────────────────────────────────────────────────
const scoreColor = (s) => {
  if (s === null || s === undefined) return 'var(--text-muted)';
  if (s >= 85) return 'var(--gold-primary)';
  if (s >= 70) return 'var(--c-success)';
  if (s >= 55) return 'var(--c-warning)';
  return 'var(--c-danger)';
};
const scoreGrade = (s) => {
  if (s === null || s === undefined) return { label: 'No Data',    color: 'var(--text-muted)' };
  if (s >= 85) return { label: 'Excellent', color: 'var(--gold-primary)' };
  if (s >= 70) return { label: 'Good',      color: 'var(--c-success)' };
  if (s >= 55) return { label: 'Average',   color: 'var(--c-warning)' };
  return              { label: 'Needs Work',color: 'var(--c-danger)' };
};

// ── SVG circular score ring ───────────────────────────────────────────────────
const ScoreRing = ({ score, size = 148, stroke = 11 }) => {
  const r     = (size - stroke * 2) / 2;
  const circ  = 2 * Math.PI * r;
  const pct   = score !== null ? Math.max(0, Math.min(100, score)) : 0;
  const off   = circ - (circ * pct) / 100;
  const clr   = scoreColor(score);
  const grade = scoreGrade(score);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke="var(--border)" strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke={clr} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={off}
          style={{ transition: 'stroke-dashoffset 1.1s cubic-bezier(.4,0,.2,1), stroke 0.4s' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <span style={{ fontSize: 30, fontWeight: 800, color: clr, lineHeight: 1 }}>
          {score !== null ? score.toFixed(1) : '—'}
        </span>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.07em',
          textTransform: 'uppercase', color: grade.color }}>
          {grade.label}
        </span>
      </div>
    </div>
  );
};

// ── Trend delta badge ─────────────────────────────────────────────────────────
const TrendBadge = ({ current, previous }) => {
  if (current === null || previous === null) return null;
  const diff = current - previous;
  if (Math.abs(diff) < 0.05) return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11,
      fontWeight: 700, color: 'var(--text-muted)', background: 'var(--bg-surface-3)',
      padding: '2px 7px', borderRadius: 6 }}>
      <Minus size={10} /> 0.0%
    </span>
  );
  const up = diff > 0;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11,
      fontWeight: 700, padding: '2px 7px', borderRadius: 6,
      color: up ? 'var(--c-success)' : 'var(--c-danger)',
      background: up ? 'var(--c-success-bg)' : 'var(--c-danger-bg)',
    }}>
      {up ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
      {Math.abs(diff).toFixed(1)}%
    </span>
  );
};

// ── Weighted score bar ────────────────────────────────────────────────────────
const ScoreBar = ({ label, avgScore, weightage, wScore }) => {
  const clr = scoreColor(avgScore);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {weightage !== null && (
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)',
              background: 'var(--bg-surface-3)', padding: '1px 6px', borderRadius: 4 }}>
              wt {fmt(weightage)}
            </span>
          )}
          <span style={{ fontSize: 13, fontWeight: 800, color: clr, minWidth: 52, textAlign: 'right' }}>
            {fmt(wScore ?? avgScore, '%', 2)}
          </span>
        </div>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: 'var(--border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 999, background: clr,
          width: `${Math.max(0, Math.min(100, avgScore ?? 0))}%`,
          transition: 'width 0.9s cubic-bezier(.4,0,.2,1)' }} />
      </div>
    </div>
  );
};

// ── Metric row inside a category card ────────────────────────────────────────
const MRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1, paddingRight: 8 }}>{label}</span>
    <span style={{ fontSize: 12, fontWeight: 700, color: color || 'var(--text-primary)', flexShrink: 0 }}>{value}</span>
  </div>
);

// ── Category card ─────────────────────────────────────────────────────────────
const CatCard = ({ title, icon: Icon, iconBg, iconColor, avgScore, children }) => (
  <div className="k-card p-4">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0,
          background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} style={{ color: iconColor }} />
        </div>
        <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{title}</span>
      </div>
      <span style={{ fontSize: 18, fontWeight: 800, color: scoreColor(avgScore) }}>
        {fmt(avgScore, '%', 1)}
      </span>
    </div>
    {children}
  </div>
);

// ── SVG Trend Chart ───────────────────────────────────────────────────────────
const LINES = [
  { col: C.OVERALL, label: 'Overall',    cssVar: '--gold-primary', w: 2.5,  main: true },
  { col: C.AVG_FMS, label: 'FMS',        cssVar: '--teal-primary', w: 1.3,  main: false },
  { col: C.AVG_HT,  label: 'HelpTicket', cssVar: '--c-info',       w: 1.3,  main: false },
  { col: C.AVG_DEL, label: 'Delegation', cssVar: '--c-purple',     w: 1.3,  main: false },
  { col: C.AVG_CL,  label: 'Checklist',  cssVar: '--c-success',    w: 1.3,  main: false },
];

const TrendChart = ({ allRows, selectedAllRowsIdx, onDotClick }) => {
  const [hovered, setHovered] = useState(null);

  // Show last 12 weeks oldest→newest
  const chartRows = useMemo(
    () => allRows.slice(0, Math.min(allRows.length, 12)).reverse(),
    [allRows]
  );

  if (chartRows.length < 2) return (
    <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>
      Need at least 2 weeks of data to show a trend.
    </p>
  );

  const W = 640, H = 168;
  const PAD = { t: 22, r: 16, b: 30, l: 32 };
  const iW = W - PAD.l - PAD.r;
  const iH = H - PAD.t - PAD.b;

  const toX = (i) => PAD.l + (i / (chartRows.length - 1)) * iW;
  const toY = (v) => v !== null ? PAD.t + iH * (1 - v / 100) : null;

  // Which chart index corresponds to selectedAllRowsIdx?
  const selChartIdx = selectedAllRowsIdx < chartRows.length
    ? chartRows.length - 1 - selectedAllRowsIdx
    : null;

  const handleClick = (ci) => onDotClick(chartRows.length - 1 - ci);

  // Build point sets for each line
  const pointSets = LINES.map(line => ({
    ...line,
    pts: chartRows.map((r, i) => ({ x: toX(i), y: toY(n(r, line.col)), val: n(r, line.col), i }),),
  }));

  // Area fill points for main line
  const mainPts = pointSets[0].pts.filter(p => p.val !== null);
  const areaPath = mainPts.length >= 2
    ? [`${mainPts[0].x},${PAD.t + iH}`, ...mainPts.map(p => `${p.x},${p.y}`), `${mainPts[mainPts.length-1].x},${PAD.t + iH}`].join(' ')
    : null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', fontFamily: 'inherit', overflow: 'visible' }}>
      <defs>
        <linearGradient id="misAreaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="var(--gold-primary)" stopOpacity="0.20" />
          <stop offset="100%" stopColor="var(--gold-primary)" stopOpacity="0.01" />
        </linearGradient>
      </defs>

      {/* Y grid */}
      {[0, 25, 50, 75, 100].map(v => (
        <g key={v}>
          <line x1={PAD.l} x2={W - PAD.r} y1={toY(v)} y2={toY(v)}
            stroke="var(--border)" strokeWidth={v === 0 ? 1 : 0.7}
            strokeDasharray={v === 0 ? undefined : '3,3'} />
          <text x={PAD.l - 5} y={toY(v) + 3.5} textAnchor="end"
            fontSize={8} fill="var(--text-muted)">{v}</text>
        </g>
      ))}

      {/* Area fill */}
      {areaPath && <polygon points={areaPath} fill="url(#misAreaGrad)" />}


      {/* Overall main line */}
      {mainPts.length >= 2 && (
        <polyline points={mainPts.map(p => `${p.x},${p.y}`).join(' ')}
          fill="none" stroke="var(--gold-primary)" strokeWidth={2.5}
          strokeLinejoin="round" strokeLinecap="round" />
      )}

      {/* Hover column */}
      {hovered !== null && (
        <line x1={toX(hovered)} x2={toX(hovered)} y1={PAD.t} y2={PAD.t + iH}
          stroke="var(--border-strong)" strokeWidth={1} strokeDasharray="3,3" />
      )}

      {/* Overall dots (interactive) */}
      {pointSets[0].pts.map(pt => {
        if (pt.val === null) return null;
        const isSel   = selChartIdx === pt.i;
        const isHov   = hovered === pt.i;
        const r       = isSel ? 5.5 : isHov ? 4.5 : 3.5;
        const showLbl = isSel || isHov;
        return (
          <g key={pt.i} style={{ cursor: 'pointer' }}
            onClick={() => handleClick(pt.i)}
            onMouseEnter={() => setHovered(pt.i)}
            onMouseLeave={() => setHovered(null)}>
            {/* Larger invisible click target */}
            <circle cx={pt.x} cy={pt.y} r={12} fill="transparent" />
            <circle cx={pt.x} cy={pt.y} r={r}
              fill={scoreColor(pt.val)}
              stroke="var(--bg-surface)" strokeWidth={isSel ? 2.5 : 2} />
            {showLbl && (
              <text x={pt.x} y={pt.y - 9} textAnchor="middle"
                fontSize={9} fontWeight="700" fill={scoreColor(pt.val)}>
                {pt.val.toFixed(1)}
              </text>
            )}
          </g>
        );
      })}

      {/* X axis labels */}
      {chartRows.map((r, i) => (
        <text key={i} x={toX(i)} y={H - 4} textAnchor="middle" fontSize={8}
          fill={selChartIdx === i ? 'var(--gold-primary)' : 'var(--text-muted)'}
          fontWeight={selChartIdx === i ? '700' : '400'}>
          W{r[C.WEEK]}
        </text>
      ))}
    </svg>
  );
};


// ── Main Analytics component ──────────────────────────────────────────────────
const Analytics = ({ currentUser }) => {
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [allRows,     setAllRows]     = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        `/api/sheets?sheetId=${MIS_SHEET_ID}&sheetName=${encodeURIComponent(MIS_SHEET_NAME)}&range=A:CK`
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${resp.status}`);
      }
      const json = await resp.json();
      const rows = json.values || [];
      if (rows.length < 2) { setAllRows([]); return; }

      const userName = (currentUser.name || '').trim().toLowerCase();
      const userRows = rows.slice(1).filter(
        row => (row[C.NAME] || '').trim().toLowerCase() === userName
      );
      // Sort newest first: Year desc → Month desc → Week desc
      userRows.sort((a, b) => {
        const dy = (parseInt(b[C.YEAR]) || 0) - (parseInt(a[C.YEAR]) || 0);
        if (dy !== 0) return dy;
        const dm = (parseInt(b[C.MONTH]) || 0) - (parseInt(a[C.MONTH]) || 0);
        if (dm !== 0) return dm;
        return (parseInt(b[C.WEEK]) || 0) - (parseInt(a[C.WEEK]) || 0);
      });
      setAllRows(userRows);
      setSelectedIdx(0);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser.name]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const row  = allRows[selectedIdx]     || null;
  const prev = allRows[selectedIdx + 1] || null;

  // Scores for selected week
  const overall     = n(row, C.OVERALL);
  const prevOverall = n(prev, C.OVERALL);
  const avgFMS      = n(row, C.AVG_FMS);
  const avgHT       = n(row, C.AVG_HT);
  const avgDel      = n(row, C.AVG_DEL);
  const avgCL       = n(row, C.AVG_CL);
  const salesNotAch = n(row, C.SALES_NOT_ACH);
  const avgSales    = salesNotAch !== null ? 100 - salesNotAch : null;

  const status = row?.[C.STATUS] || '';
  const isDone = status.toLowerCase().includes('done');

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="k-page">

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
            My Performance Score
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Employee MIS — {currentUser.name}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Week selector dropdown */}
          {allRows.length > 0 && (
            <div style={{ position: 'relative' }}>
              <select
                value={selectedIdx}
                onChange={e => setSelectedIdx(Number(e.target.value))}
                style={{
                  appearance: 'none', WebkitAppearance: 'none',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 8, cursor: 'pointer', outline: 'none',
                  padding: '7px 32px 7px 11px',
                  fontSize: 12, fontWeight: 600,
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                  boxShadow: 'var(--shadow-xs)',
                }}
              >
                {allRows.map((r, i) => (
                  <option key={i} value={i}>
                    Week {r[C.WEEK]} — {r[C.MONTH]}/{r[C.YEAR]}
                    {(r[C.STATUS] || '').toLowerCase().includes('done') ? ' ✓' : ''}
                  </option>
                ))}
              </select>
              <ChevronDown size={13} style={{
                position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-muted)', pointerEvents: 'none',
              }} />
            </div>
          )}
          {lastRefresh && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button className="k-refresh-btn w-8 h-8" onClick={fetchData} disabled={loading} title="Refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className="k-card p-10" style={{ textAlign: 'center' }}>
          <Loader size={34} className="animate-spin"
            style={{ color: 'var(--gold-primary)', margin: '0 auto 14px' }} />
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading your performance data…</p>
        </div>
      )}

      {/* ── Error ── */}
      {!loading && error && (
        <div className="k-card p-6" style={{ borderColor: 'var(--c-danger)', textAlign: 'center' }}>
          <AlertTriangle size={34} style={{ color: 'var(--c-danger)', margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--c-danger)', fontWeight: 700, marginBottom: 4 }}>Failed to load score data</p>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{error}</p>
          <button className="k-btn k-btn-ghost" onClick={fetchData}
            style={{ margin: '16px auto 0', display: 'inline-flex', gap: 6 }}>
            <RefreshCw size={13} /> Retry
          </button>
        </div>
      )}

      {/* ── No data ── */}
      {!loading && !error && allRows.length === 0 && (
        <div className="k-card p-10" style={{ textAlign: 'center' }}>
          <BarChart3 size={42} style={{ color: 'var(--text-muted)', margin: '0 auto 14px' }} />
          <p style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>No Score Records Found</p>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            No MIS score records found for <strong>{currentUser.name}</strong> in TESTMASTER.
          </p>
        </div>
      )}

      {/* ── Dashboard ── */}
      {!loading && !error && allRows.length > 0 && (
        <>
          {/* Pending notice */}
          {!isDone && (
            <div className="k-card p-3" style={{ background: 'var(--c-warning-bg)',
              borderColor: 'var(--c-warning)', display: 'flex', alignItems: 'center',
              gap: 8, marginBottom: 14 }}>
              <AlertTriangle size={15} style={{ color: 'var(--c-warning)', flexShrink: 0 }} />
              <p style={{ fontSize: 12, color: 'var(--c-warning)', margin: 0, fontWeight: 500 }}>
                Score for this week is not finalised yet{status ? ` (${status})` : ''}.
              </p>
            </div>
          )}

          {/* ── Trend Chart ── */}
          <div className="k-card p-5" style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
              <div>
                <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.09em',
                  textTransform: 'uppercase', color: 'var(--text-muted)', margin: 0 }}>
                  Performance Trend
                </p>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '3px 0 0', fontWeight: 500 }}>
                  {Math.min(allRows.length, 12)} most recent weeks · click a dot to inspect
                </p>
              </div>
            </div>
            <TrendChart
              allRows={allRows}
              selectedAllRowsIdx={selectedIdx}
              onDotClick={setSelectedIdx}
            />
          </div>

          {/* ── Hero: Score ring + breakdown ── */}
          <div className="k-card p-6" style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Ring side */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <ScoreRing score={overall} />
                <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'var(--text-muted)', margin: 0 }}>
                  Overall MIS Score
                </p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
                    background: 'var(--bg-surface-3)', padding: '2px 8px', borderRadius: 6,
                  }}>
                    <Calendar size={9} /> Week {row?.[C.WEEK]} · {row?.[C.MONTH]}/{row?.[C.YEAR]}
                  </span>
                  <TrendBadge current={overall} previous={prevOverall} />
                </div>
              </div>

              {/* Divider */}
              <div style={{ width: 1, alignSelf: 'stretch', minHeight: 120,
                background: 'var(--border)', flexShrink: 0 }} />

              {/* Breakdown bars */}
              <div style={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 9 }}>
                <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'var(--text-muted)', margin: '0 0 2px' }}>
                  Weighted Breakdown
                </p>
                <ScoreBar label="FMS Tasks"    avgScore={avgFMS}   weightage={n(row,C.WGT_FMS)}   wScore={n(row,C.WSCORE_FMS)} />
                <ScoreBar label="Help Tickets" avgScore={avgHT}    weightage={n(row,C.WGT_HT)}    wScore={n(row,C.WSCORE_HT)} />
                <ScoreBar label="Delegation"   avgScore={avgDel}   weightage={n(row,C.WGT_DEL)}   wScore={n(row,C.WSCORE_DEL)} />
                <ScoreBar label="Checklist"    avgScore={avgCL}    weightage={n(row,C.WGT_CL)}    wScore={n(row,C.WSCORE_CL)} />
                <ScoreBar label="Sales"        avgScore={avgSales} weightage={n(row,C.WGT_SALES)} wScore={n(row,C.WSCORE_SALES)} />
              </div>
            </div>
          </div>

          {/* ── Category detail cards ── */}
          <div style={{ display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(256px, 1fr))', gap: 12 }}>

            {/* FMS */}
            <CatCard title="FMS Tasks" icon={Activity}
              iconBg="var(--teal-dim)" iconColor="var(--teal-primary)" avgScore={avgFMS}>
              <MRow label="Work Done"
                value={`${fmt(n(row,C.FMS_WD_ACT),'',0)} / ${fmt(n(row,C.FMS_WD_PLAN),'',0)}`} />
              <MRow label="Work Not Done %"      value={fmt(n(row,C.FMS_WD_PCT))}
                color={(() => { const v=n(row,C.FMS_WD_PCT); return v!==null?scoreColor(100-v):'var(--text-muted)'; })()} />
              <MRow label="Not Done On Time %"   value={fmt(n(row,C.FMS_OT_PCT))}
                color={(() => { const v=n(row,C.FMS_OT_PCT); return v!==null?scoreColor(100-v):'var(--text-muted)'; })()} />
              <MRow label="Delayed %"            value={fmt(n(row,C.FMS_DEL_PCT))}
                color={(() => { const v=n(row,C.FMS_DEL_PCT); return v!==null?scoreColor(100-v):'var(--text-muted)'; })()} />
            </CatCard>

            {/* HelpTicket */}
            <CatCard title="Help Tickets" icon={Zap}
              iconBg="var(--c-info-bg)" iconColor="var(--c-info)" avgScore={avgHT}>
              <MRow label="Tickets Done"
                value={`${fmt(n(row,C.HT_WD_ACT),'',0)} / ${fmt(n(row,C.HT_WD_PLAN),'',0)}`} />
              <MRow label="Work Not Done %"      value={fmt(n(row,C.HT_WD_PCT))}
                color={(() => { const v=n(row,C.HT_WD_PCT); return v!==null?scoreColor(100-v):'var(--text-muted)'; })()} />
              <MRow label="Not Done On Time %"   value={fmt(n(row,C.HT_OT_PCT))}
                color={(() => { const v=n(row,C.HT_OT_PCT); return v!==null?scoreColor(100-v):'var(--text-muted)'; })()} />
              <MRow label="Delayed %"            value={fmt(n(row,C.HT_DEL_PCT))}
                color={(() => { const v=n(row,C.HT_DEL_PCT); return v!==null?scoreColor(100-v):'var(--text-muted)'; })()} />
            </CatCard>

            {/* Delegation */}
            <CatCard title="Delegation" icon={Users}
              iconBg="var(--c-purple-bg)" iconColor="var(--c-purple)" avgScore={avgDel}>
              <MRow label="Tasks"
                value={`${fmt(n(row,C.DEL_ACT),'',0)} / ${fmt(n(row,C.DEL_PLAN),'',0)}`} />
              <MRow label="Completion %"
                value={(() => { const v=n(row,C.DEL_PCT); return v!==null?fmt(v*100):'—'; })()}
                color={(() => { const v=n(row,C.DEL_PCT); return v!==null?scoreColor(v*100):'var(--text-muted)'; })()} />
              <MRow label="Green %"  value={fmt(n(row,C.DEL_GRN))} color="var(--c-success)" />
              <MRow label="Yellow %" value={fmt(n(row,C.DEL_YEL))}
                color={(() => { const v=n(row,C.DEL_YEL); return v!==null?scoreColor(100-v):'var(--text-muted)'; })()} />
              <MRow label="Red %"    value={fmt(n(row,C.DEL_RED))}
                color={(() => { const v=n(row,C.DEL_RED); return v!==null?scoreColor(100-v):'var(--text-muted)'; })()} />
            </CatCard>

            {/* Checklist */}
            <CatCard title="Checklist" icon={CheckCircle2}
              iconBg="var(--green-dim)" iconColor="var(--green-primary)" avgScore={avgCL}>
              <MRow label="Tasks Planned"     value={fmt(n(row,C.CL_PLAN),'',0)} />
              <MRow label="Tasks Actual"      value={fmt(n(row,C.CL_ACT),'',0)} />
              <MRow label="% Not Done"        value={fmt(n(row,C.CL_NOT_DONE))}
                color={(() => { const v=n(row,C.CL_NOT_DONE); return v!==null?scoreColor(100-v):'var(--text-muted)'; })()} />
              <MRow label="% Not Done On Time" value={fmt(n(row,C.CL_NOT_DONE_OT))}
                color={(() => { const v=n(row,C.CL_NOT_DONE_OT); return v!==null?scoreColor(100-v):'var(--text-muted)'; })()} />
            </CatCard>

            {/* Sales */}
            <CatCard title="Sales" icon={TrendingUp}
              iconBg="var(--gold-dim)" iconColor="var(--gold-primary)" avgScore={avgSales}>
              <MRow label="Total Target"           value={fmtN(n(row,C.SALES_TOT_TGT))} />
              <MRow label="Total Achieved"         value={fmtN(n(row,C.SALES_TOT_ACH))} color={scoreColor(avgSales)} />
              <MRow label="New  Tgt / Achieved"    value={`${fmtN(n(row,C.SALES_NEW_TGT))} / ${fmtN(n(row,C.SALES_NEW_ACH))}`} />
              <MRow label="Exist Tgt / Achieved"   value={`${fmtN(n(row,C.SALES_EX_TGT))} / ${fmtN(n(row,C.SALES_EX_ACH))}`} />
              <MRow label="% Not Achieved"         value={fmt(salesNotAch)}
                color={salesNotAch!==null?scoreColor(100-salesNotAch):'var(--text-muted)'} />
            </CatCard>

          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
